import { zodResolver } from "@hookform/resolvers/zod";
import api from "@/lib/api.ts";
import { Button, PasswordInput, Text, Title } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z, ZodType } from "zod";
import { notifications } from "@mantine/notifications";

type ResetPasswordForm = {
  password: string;
  password_confirmation: string;
};

const resetPasswordFormSchema: ZodType<ResetPasswordForm> = z
  .object({
    password: z.string().min(8, {
      message: "Password must be at least 8 characters long",
    }),
    password_confirmation: z.string().min(8, {
      message: "Password must be at least 8 characters long",
    }),
  })
  .refine(
    ({ password_confirmation, password }) => password === password_confirmation,
    {
      message: "Password confirmation does not match",
      path: ["password_confirmation"],
    },
  );

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      navigate("/auth/login");
    }
  });

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      password: "",
      password_confirmation: "",
    },
  });
  const errors = form.formState.errors;

  const mutation = useMutation({
    mutationFn: (resetPasswordForm: ResetPasswordForm) => {
      return api.post("auth/reset-password", {
        ...resetPasswordForm,
        token,
        email,
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Password reset successfully",
        message: "Use your new password to sign in.",
      });
      navigate("/");
    },
    retry: 1,
  });

  const onSubmit: SubmitHandler<ResetPasswordForm> = (data) => {
    mutation.mutate(data);
  };

  return (
    <div className="mx-auto grid w-[350px] gap-6">
      <div className="grid gap-2 text-center">
        <Title order={1}>Reset password</Title>
        <Text c="gray">Enter your new password</Text>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
        <PasswordInput
          {...form.register("password")}
          name="password"
          withAsterisk
          label="Password"
          size="md"
          error={errors.password?.message}
        />

        <PasswordInput
          {...form.register("password_confirmation")}
          name="password_confirmation"
          withAsterisk
          label="Password confirmation"
          size="md"
          error={errors.password_confirmation?.message}
        />

        <Button
          loading={mutation.isPending}
          disabled={mutation.isPending}
          type="submit"
          fullWidth
          size="md"
        >
          Reset
        </Button>
      </form>

      <Text ta="center">
        Oh, you did remember your password?{" "}
        <Link to="/auth/login" className="underline">
          Sign in
        </Link>
      </Text>
    </div>
  );
}
